/**
 * LangToggle — Iter314 refactor.
 *
 * Previous behavior: click cycled EN → PT → ES with a globe icon.
 * New behavior: shows the current-language FLAG. Clicking opens a small
 * popover with all three languages plus a link to the full Language
 * Preference panel in Account Settings.
 *
 * Guardrails:
 *   • The flag glyph is a Unicode emoji, not decorative text, so it does
 *     NOT trip the react/jsx-no-literals ESLint guardrail (emoji is a
 *     graphic, not translatable copy).
 *   • Every visible label goes through `t()` so the popover itself is
 *     localized in EN/PT/ES.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Settings2, Check } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const LANGS = [
  { code: "en", flag: "\u{1F1FA}\u{1F1F8}", label: "English" },   // 🇺🇸
  { code: "pt", flag: "\u{1F1E7}\u{1F1F7}", label: "Português" }, // 🇧🇷
  { code: "es", flag: "\u{1F1EA}\u{1F1F8}", label: "Español" },   // 🇪🇸
];

export default function LangToggle({ className = "", testid = "lang-toggle" }) {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div className={"relative inline-block " + className} ref={wrapRef}>
      <button
        type="button"
        data-testid={testid}
        aria-label={t("lang.picker_open")}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title={current.label}
        className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
      >
        <span className="text-[18px] leading-none" aria-hidden="true">{current.flag}</span>
        <span className="uppercase text-[11px] text-[#6B7280]">{lang}</span>
      </button>
      {open && (
        <div
          data-testid="lang-picker-popover"
          className="absolute right-0 top-11 z-50 w-56 rounded-xl border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-2xl p-1.5"
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-[var(--al-text-muted)]">
            {t("lang.picker_title")}
          </div>
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              data-testid={`lang-picker-${l.code}`}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
              }}
              className={
                "w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] " +
                (l.code === lang
                  ? "bg-[#3B82F6]/10 text-[#3B82F6] font-semibold"
                  : "text-[var(--al-text)] hover:bg-white/5")
              }
            >
              <span className="text-[18px] leading-none" aria-hidden="true">{l.flag}</span>
              <span className="flex-1 text-left">{l.label}</span>
              {l.code === lang && <Check className="w-4 h-4" />}
            </button>
          ))}
          <div className="mt-1 pt-1 border-t border-[var(--al-border)]">
            <Link
              to="/account#language"
              data-testid="lang-picker-settings-link"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12.5px] text-[var(--al-text-muted)] hover:text-[#3B82F6] hover:bg-white/5"
            >
              <Settings2 className="w-3.5 h-3.5" />
              {t("lang.picker_settings")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
